import { useMemo } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useRequiredInfoValidation } from './useRequiredInfoValidation';
import { describeMissingRequiredInfo } from './requiredInfoValidation';
import { getRequiredPropertyFieldSummaries } from '../property/propertyRequirements';

export type InternalStepValidationResult = {
  isValid: boolean;
  missingFields: string[];
  errors: Record<string, string>;
};

/**
 * Hook to validate internal steps within a main step
 * Returns validation status, missing fields, and field-specific errors
 * @param mainStep - The main step number (1-5)
 * @param internalStep - The logical validation case (0-4), not necessarily the array index
 * @param hasAddonStep - Whether addon step exists (for PropertyStep dynamic steps)
 */
export function useInternalStepValidation(
  mainStep: number,
  internalStep: number,
  hasAddonStep: boolean = false
): InternalStepValidationResult {
  const {
    selectedPrimaryService,
    contact,
    address,
    selectedRoleIds,
  } = useScheduler();

  // Shared required-info rule — resolves the mandatory questions for
  // the selected services rather than inspecting whatever answers happen to
  // exist already.
  const requiredInfo = useRequiredInfoValidation();

  return useMemo(() => {
    const missingFields: string[] = [];
    const errors: Record<string, string> = {};

    switch (mainStep) {
      case 1: // Services Step
        switch (internalStep) {
          case 0: // Filter step - check if filtering has been completed
            if (!selectedRoleIds || selectedRoleIds.length === 0) {
              missingFields.push('Complete the service filter');
            }
            break;

          case 1: // Select Primary Service
            if (!selectedPrimaryService) {
              missingFields.push('Select a primary service');
            }
            // Note: Role selection happens in step 2 (ContactForm), not here
            break;

          case 2: // Contact Form
            if (!contact) {
              missingFields.push('Enter contact information');
            } else {
              if (!contact.firstName || contact.firstName.trim() === '') {
                missingFields.push('First name');
                errors.firstName = 'First name is required';
              }
              if (!contact.lastName || contact.lastName.trim() === '') {
                missingFields.push('Last name');
                errors.lastName = 'Last name is required';
              }
              if (!contact.email || contact.email.trim() === '') {
                missingFields.push('Email address');
                errors.email = 'Email address is required';
              } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
                errors.email = 'Invalid email format';
              }
              if (!contact.phone || contact.phone.trim() === '') {
                missingFields.push('Phone number');
                errors.phone = 'Phone number is required';
              }
            }
            break;

          default:
            return { isValid: true, missingFields: [], errors: {} };
        }
        break;

      case 2: // Property Step
        switch (internalStep) {
          case 0: // Find Property (always index 0)
            if (!address) {
              missingFields.push('Enter property address');
            } else {
              if (!address.street || address.street.trim() === '') {
                missingFields.push('Street address');
                errors.street = 'Street address is required';
              }
              if (!address.city || address.city.trim() === '') {
                missingFields.push('City');
                errors.city = 'City is required';
              }
              if (!address.state || address.state.trim() === '') {
                missingFields.push('State');
                errors.state = 'State is required';
              }
              if (!address.zip || address.zip.trim() === '') {
                missingFields.push('ZIP code');
                errors.zip = 'ZIP code is required';
              }
            }
            break;

          case 1: // Review Property Details (always index 1)
            // Ensure step 0 is complete (address with all required fields)
            if (!address) {
              missingFields.push('Enter property address');
            } else {
              if (!address.street || address.street.trim() === '') {
                missingFields.push('Street address');
              }
              if (!address.city || address.city.trim() === '') {
                missingFields.push('City');
              }
              if (!address.state || address.state.trim() === '') {
                missingFields.push('State');
              }
              if (!address.zip || address.zip.trim() === '') {
                missingFields.push('ZIP code');
              }
              // Square Footage, Year Built and Foundation are what this step
              // exists to confirm, and the first two feed the `modifier-sqft` /
              // `modifier-age` price modifiers — advancing without them books a
              // silently mispriced job. The rule lives in `propertyRequirements`
              // so the blocking gate and the on-screen alert cannot disagree.
              // Only reported once the address itself is present: with no
              // address every field is trivially missing, and listing them
              // alongside "Enter property address" reads as four problems
              // instead of one.
              missingFields.push(...getRequiredPropertyFieldSummaries(address));
            }
            break;

          case 2: // Addon Recommendations (if exists) OR More Information (if no addons)
            // This is a logical case that maps to different array indices:
            // - If hasAddonStep: maps to array index 2 (Addon Recommendations)
            // - If !hasAddonStep: maps to array index 2 (More Information)
            if (hasAddonStep) {
              // This is Addon Recommendations step (optional, no validation needed)
              return { isValid: true, missingFields: [], errors: {} };
            }
            // This is the More Information step (when no addons exist)
            missingFields.push(
              ...describeMissingRequiredInfo(requiredInfo.missingItems)
            );
            break;

          case 3: // More Information (only reached if addons step exists)
            // This logical case only exists when hasAddonStep is true
            // It maps to array index 3 (More Information)
            if (!hasAddonStep) {
              // This case shouldn't be validated if addon step doesn't exist
              return { isValid: true, missingFields: [], errors: {} };
            }
            missingFields.push(
              ...describeMissingRequiredInfo(requiredInfo.missingItems)
            );
            // Note: Property details (bedrooms, bathrooms, etc.) are optional
            break;

          default:
            return { isValid: true, missingFields: [], errors: {} };
        }
        break;

      case 3: // Schedule Step
        // No internal steps, validated at main level
        return { isValid: true, missingFields: [], errors: {} };

      case 4: // Contact Step (Additional Contacts)
        // Validate that all required roles have contacts
        // This is handled in the component itself
        return { isValid: true, missingFields: [], errors: {} };

      case 5: // Summary Step
        // No internal steps, validated at main level
        return { isValid: true, missingFields: [], errors: {} };

      default:
        return { isValid: true, missingFields: [], errors: {} };
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
      errors,
    };
  }, [
    mainStep,
    internalStep,
    hasAddonStep,
    selectedPrimaryService,
    contact,
    address,
    requiredInfo,
    selectedRoleIds,
  ]);
}
